<template>
  <BuilderWrapper title="Cluster and Heading Positions">
    <p>
      Click and drag the
      <span class="larger">
        &#129132;
      </span>
      handle to position your cluster of bubbles
    </p>
    <p>
      Click and drag the
      <b-button size="sm">
        Headings
      </b-button>
      to position them
    </p>

    <LabelledInput
      label="Global Layout"
      helpful-tip="These settings apply to all tabs at once"
      label-style="Description"
    >
      <b-alert
        show
        variant="warning"
      >
        <b-icon icon="exclamation-triangle" />These settings will be applied to all tabs, will override manual changes and cannot be undone
      </b-alert>

      <b-button-group
        vertical
        class="btn-block"
      >
        <b-button
          variant="outline-primary"
          @click="autoLayoutBubblesClusters(SortClustersByOptions.LARGEST_TO_SMALLEST)"
        >
          Largest to Smallest Cluster
        </b-button>
        <b-button
          variant="outline-primary"
          @click="autoLayoutBubblesClusters(SortClustersByOptions.SMALLEST_TO_LARGEST)"
        >
          Smallest to Largest Cluster
        </b-button>
        <b-button
          variant="outline-primary"
          @click="autoLayoutBubblesClusters(SortClustersByOptions.ALPHABETICAL)"
        >
          Ascending Header's Name
        </b-button>
        <b-button
          variant="outline-primary"
          @click="autoLayoutBubblesClusters(SortClustersByOptions.REVERSE_ALPHABETICAL)"
        >
          Descending Header's Name
        </b-button>
      </b-button-group>
    </LabelledInput>
  </BuilderWrapper>
</template>

<script lang="ts">

import Vue from 'vue'
import Component from 'vue-class-component'

import BuilderWrapper from '@/components/builder/UI/BuilderWrapper.vue'
import LabelledInput from '@/components/builder/UI/LabelledInput.vue'
import ClusterAndHeaderBox, { SortClustersByOption, SortClustersByOptions } from '@/components/public/Bubbles/ClusterAndHeaderBox'
import { NODE_CONFIG_KEY, VIEWER_LEVEL_SUBROOT, VISUALISATION_VIEWBOX_POSITION_OFFSET, VISUALISATION_VIEWBOX_SIZE, defaultLevelBasedPositions } from '@/constants'
import { NodeConfigKeys } from '@/types'
import _cloneDeep from 'lodash/cloneDeep'

const components = {
  BuilderWrapper,
  LabelledInput,
}

@Component({ components })
export default class BubblesClusterAndHeadingPositions extends Vue {
  SortClustersByOptions = SortClustersByOptions

  autoLayoutBubblesClusters (sortBy: SortClustersByOption) {
    const clusterCollectionsByLevel = this.$typedStore.visualisationData.clusterCollectionsByLevel

    clusterCollectionsByLevel.forEach((collections, level) => {
      collections.forEach((collection) => {
        collection.sort(sortBy)
        collection.calculateAutoLayoutPositions()

        for (const box of collection) {
          this.updateClusterBubblePositions(box, level)
          this.updateClusterHeaderPositions(box, level)
        }
      })
    })
  }

  private updateClusterBubblePositions (box: ClusterAndHeaderBox, level: number) {
    const leftPositionLimit = VISUALISATION_VIEWBOX_POSITION_OFFSET
    const rightPositionLimit = VISUALISATION_VIEWBOX_SIZE - VISUALISATION_VIEWBOX_POSITION_OFFSET

    // Update cluster if it's within the bounds of the visualisation viewbox
    if (box.cluster.centerX !== undefined &&
        box.cluster.centerX > leftPositionLimit && box.cluster.centerX < rightPositionLimit &&
        box.cluster.centerY !== undefined &&
        box.cluster.centerY > leftPositionLimit && box.cluster.centerY < rightPositionLimit) {
      const bubblePositions = _cloneDeep(this.$typedStore.primary.entities.nodes.bubblePositions(box.path)) ?? defaultLevelBasedPositions()
      const position = {
        x: box.cluster.centerX,
        y: box.cluster.centerY,
      }

      // If the bubbles are being configured at the subroot level
      // then set their position as a level override
      if (level === VIEWER_LEVEL_SUBROOT) {
        if (!bubblePositions.levelOverrides) {
          bubblePositions.levelOverrides = {}
        }
        bubblePositions.levelOverrides[level] = position
      } else {
        bubblePositions.default = position
      }

      this.$typedStore.primary.entities.nodes.updateNodeProp({
        path: box.path,
        configPath: [NODE_CONFIG_KEY, NodeConfigKeys.BUBBLE_POSITIONS],
        value: bubblePositions,
      })
    }
  }

  private updateClusterHeaderPositions (box: ClusterAndHeaderBox, level: number) {
    const leftPositionLimit = VISUALISATION_VIEWBOX_POSITION_OFFSET
    const rightPositionLimit = VISUALISATION_VIEWBOX_SIZE - VISUALISATION_VIEWBOX_POSITION_OFFSET

    // Update header if it's within the bounds of the visualisation viewbox
    if (box.header.centerX !== undefined &&
        box.header.centerX > leftPositionLimit && box.header.centerX < rightPositionLimit &&
        box.header.centerY !== undefined &&
        box.header.centerY > leftPositionLimit && box.header.centerY < rightPositionLimit) {
      const headerPositions = _cloneDeep(this.$typedStore.primary.entities.nodes.headerPositions(box.path)) ?? defaultLevelBasedPositions()
      const position = {
        x: box.header.centerX,
        y: box.header.centerY,
      }

      // If the headers are being configured at the subroot level
      // then set their position as a level override
      if (level === VIEWER_LEVEL_SUBROOT) {
        if (!headerPositions.levelOverrides) {
          headerPositions.levelOverrides = {}
        }
        headerPositions.levelOverrides[level] = position
      } else {
        headerPositions.default = position
      }

      this.$typedStore.primary.entities.nodes.updateNodeProp({
        path: box.path,
        configPath: [NODE_CONFIG_KEY, NodeConfigKeys.HEADER_POSITIONS],
        value: headerPositions,
      })
    }
  }
}

</script>
